import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Check, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const RULES = [
  { key: "len", label: "At least 8 characters", test: (p) => p.length >= 8 },
  { key: "upper", label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "One lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "digit", label: "One number", test: (p) => /\d/.test(p) },
];

export function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setCurrent("");
      setNext("");
      setConfirm("");
    }
  }, [open]);

  const ruleResults = RULES.map((r) => ({ ...r, ok: r.test(next) }));
  const allRulesPass = ruleResults.every((r) => r.ok);
  const matches = next.length > 0 && next === confirm;
  const sameAsCurrent = current.length > 0 && next.length > 0 && current === next;
  const canSubmit = current && allRulesPass && matches && !sameAsCurrent && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated successfully");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Change Password</DialogTitle>
          <DialogDescription>
            Choose a strong password. You'll need your current password to confirm the change.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div>
            <Label className="text-xs text-stone-600">Current password</Label>
            <Input
              type="password"
              data-testid="current-password-input"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="mt-1.5"
              autoComplete="current-password"
            />
          </div>
          <div>
            <Label className="text-xs text-stone-600">New password</Label>
            <Input
              type="password"
              data-testid="new-password-input"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="mt-1.5"
              autoComplete="new-password"
            />
          </div>
          <div>
            <Label className="text-xs text-stone-600">Confirm new password</Label>
            <Input
              type="password"
              data-testid="confirm-password-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1.5"
              autoComplete="new-password"
            />
            {confirm.length > 0 && !matches && (
              <p className="text-xs text-rose-600 mt-1.5" data-testid="mismatch-error">
                Passwords don't match.
              </p>
            )}
            {sameAsCurrent && (
              <p className="text-xs text-rose-600 mt-1.5" data-testid="same-error">
                New password must differ from current password.
              </p>
            )}
          </div>

          <div className="rounded-xl bg-stone-50 border border-stone-200 p-3.5">
            <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-stone-500 mb-2">
              Requirements
            </div>
            <ul className="space-y-1.5">
              {ruleResults.map((r) => (
                <li
                  key={r.key}
                  data-testid={`rule-${r.key}`}
                  className={`flex items-center gap-2 text-xs ${r.ok ? "text-emerald-700" : "text-stone-500"}`}
                >
                  {r.ok ? <Check size={13} strokeWidth={2.5} /> : <X size={13} strokeWidth={2} />}
                  {r.label}
                </li>
              ))}
              <li
                data-testid="rule-match"
                className={`flex items-center gap-2 text-xs ${matches ? "text-emerald-700" : "text-stone-500"}`}
              >
                {matches ? <Check size={13} strokeWidth={2.5} /> : <X size={13} strokeWidth={2} />}
                Confirmation matches
              </li>
            </ul>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="cp-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              data-testid="cp-submit-btn"
              className="bg-stone-900 hover:bg-stone-800 text-white"
            >
              {submitting ? "Updating…" : "Update Password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
